import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import AdminLayout from "../layout/AdminLayoutSewa";
import {
  adminListReviews,
  adminReviewSubmission,
  adminMarkReviewRead,
  API_BASE,
} from "../api";

function badgeClass(status) {
  const s = String(status || "PENDING").toUpperCase();
  if (s === "APPROVED") return "bg-green-100 text-green-700";
  if (s === "REJECTED") return "bg-red-100 text-red-700";
  if (s === "FLAGGED") return "bg-orange-100 text-orange-700";
  return "bg-yellow-100 text-yellow-700";
}

function safeDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

function money(v) {
  if (v === null || v === undefined || v === "") return "-";
  return Number(v || 0).toFixed(2);
}

function mapUrl(lat, lng) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return "";
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

export default function Chat() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const res = await adminListReviews({
        status: status || undefined,
        search: search || undefined,
      });
      setRows(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal load review submissions.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) =>
      [
        r.agent_name,
        r.agent_login_id,
        r.no_akaun,
        r.bil,
        r.nombor_unit,
        r.kawasan,
        r.comment,
        r.review_status,
      ]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleReview = async (row, reviewStatus) => {
    const titleMap = {
      APPROVED: "Approve submission",
      REJECTED: "Reject submission",
      FLAGGED: "Flag submission",
    };

    const { value: adminReply } = await Swal.fire({
      title: titleMap[reviewStatus] || "Review submission",
      input: "textarea",
      inputLabel: "Reply / note for agent",
      inputPlaceholder: "Masukkan komen admin...",
      inputValue: row.admin_reply || "",
      showCancelButton: true,
      confirmButtonText: "Simpan",
      cancelButtonText: "Batal",
    });

    if (adminReply === undefined) return;

    try {
      setLoading(true);
      await adminReviewSubmission(row.id, {
        review_status: reviewStatus,
        admin_reply: adminReply,
      });

      await Swal.fire({
        icon: "success",
        title: "Berjaya",
        text: "Tindakan review telah disimpan.",
      });

      await load();
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal simpan review.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (row) => {
    try {
      await adminMarkReviewRead(row.id);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AdminLayout>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Messages (Report)</h2>
            <p className="text-sm text-gray-500 mt-1">
              Semakan remarks, PTP, gambar dan lokasi yang dihantar oleh agent.
            </p>
          </div>

          <button
            onClick={load}
            className="px-4 py-2 rounded-lg border bg-white hover:bg-gray-50"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-600">Search</label>
            <input
              className="w-full border rounded-md p-2 text-sm"
              placeholder="Search any field..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Review Status</label>
            <select
              className="w-full border rounded-md p-2 text-sm bg-white"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="PENDING">PENDING</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="FLAGGED">FLAGGED</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setStatus("");
                setSearch("");
              }}
              className="w-full px-4 py-2 rounded-lg border bg-white hover:bg-gray-50"
            >
              Clear filters
            </button>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 border text-left">ID</th>
                <th className="p-2 border text-left">Sender</th>
                <th className="p-2 border text-left">Picture</th>
                <th className="p-2 border text-left">PTP</th>
                <th className="p-2 border text-left">Remarks</th>
                <th className="p-2 border text-left">Captured At</th>
                <th className="p-2 border text-left">Map</th>
                <th className="p-2 border text-left">isRead</th>
                <th className="p-2 border text-left">Created</th>
                <th className="p-2 border text-left">Type</th>
                <th className="p-2 border text-left">Status</th>
                <th className="p-2 border text-left">Reply</th>
                <th className="p-2 border text-left">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="13" className="p-3 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="13" className="p-3 text-center text-gray-500">
                    No messages found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                 const photoUrl = row.photo_url
  ? row.photo_url.startsWith("http")
    ? row.photo_url
    : `${API_BASE}${row.photo_url}`
  : "";

                  const gmap = mapUrl(row.lat, row.lng);

                  return (
                    <tr key={row.id} className="border-t align-top hover:bg-gray-50">
                      <td className="p-2 border">{row.id}</td>

                      <td className="p-2 border">
                        <div className="font-semibold">{row.agent_name || "-"}</div>
                        <div className="text-xs text-gray-500">{row.agent_login_id || "-"}</div>
                        <div className="text-xs text-gray-500">{row.agent_type || "-"}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Akaun: {row.no_akaun || "-"}
                        </div>
                        <div className="text-xs text-gray-500">BIL: {row.bil || "-"}</div>
                        <div className="text-xs text-gray-500">Unit: {row.nombor_unit || "-"}</div>
                        <div className="text-xs text-gray-500">Kawasan: {row.kawasan || "-"}</div>
                      </td>

                      <td className="p-2 border">
                        {photoUrl ? (
                          <a
                            href={photoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            View photo
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="p-2 border">
                        {row.ptp_date || row.ptp_amount || row.ptp_note ? (
                          <div className="space-y-1">
                            <div>Date: {row.ptp_date || "-"}</div>
                            <div>Amount: {money(row.ptp_amount)}</div>
                            <div className="text-xs text-gray-600">{row.ptp_note || "-"}</div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="p-2 border">
                        <div className="max-w-xs whitespace-pre-wrap break-words">
                          {row.comment || "-"}
                        </div>
                      </td>

                      <td className="p-2 border">{safeDate(row.captured_at)}</td>

                      <td className="p-2 border">
                        {gmap ? (
                          <div className="space-y-1">
                            <a
                              href={gmap}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline"
                            >
                              Open map
                            </a>
                            <div className="text-xs text-gray-500">
                              {row.lat}, {row.lng}
                            </div>
                            <div className="text-xs text-gray-500">
                              Acc: {row.accuracy ?? row.accuracy_m ?? "-"}
                            </div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="p-2 border">
                        {row.is_read_by_admin ? (
                          <span className="text-green-700 font-semibold">Yes</span>
                        ) : (
                          <button
                            onClick={() => handleMarkRead(row)}
                            className="px-2 py-1 rounded bg-yellow-100 text-yellow-700"
                          >
                            Mark read
                          </button>
                        )}
                      </td>

                      <td className="p-2 border">{safeDate(row.created_at)}</td>

                      <td className="p-2 border">
                        {row.ptp_date || row.ptp_amount ? "REMARK + PTP" : "REMARK"}
                      </td>

                      <td className="p-2 border">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${badgeClass(
                            row.review_status
                          )}`}
                        >
                          {row.review_status || "PENDING"}
                        </span>
                      </td>

                      <td className="p-2 border">
                        <div className="max-w-xs whitespace-pre-wrap break-words">
                          {row.admin_reply || "-"}
                        </div>
                      </td>

                      <td className="p-2 border">
                        <div className="flex flex-col gap-2 min-w-[120px]">
                          <button
                            onClick={() => handleReview(row, "APPROVED")}
                            className="px-3 py-1 rounded bg-green-100 text-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReview(row, "REJECTED")}
                            className="px-3 py-1 rounded bg-red-100 text-red-700"
                          >
                            Reject
                          </button>
                          <button
                            onClick={() => handleReview(row, "FLAGGED")}
                            className="px-3 py-1 rounded bg-orange-100 text-orange-700"
                          >
                            Flag
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-sm text-gray-500">
          Showing {filteredRows.length} of {rows.length} messages.
        </div>
      </div>
    </AdminLayout>
  );
}
